import { useCallback, useState } from "react";
import { useMutation, useQuery } from "urql";
import type { StakeholderRespondentType } from "../graphql/generated/graphql";
import {
  CreateStakeholderSettingMutation,
  DeleteStakeholderSettingMutation,
  StakeholderSettingsQuery,
  UpdateStakeholderSettingMutation,
} from "../graphql/stakeholderSettings.graphql";
import { toUserFacingMessage } from "../lib/graphqlErrorMessage";

export interface StakeholderSetting {
  stakeholderSettingId: string;
  termId: string;
  name: string;
  respondentType: StakeholderRespondentType;
  scriptTemplateSheetUrl: string;
  contentsTemplateSheetUrl: string;
}

export interface CreateStakeholderSettingInput {
  termId: string;
  name: string;
  respondentType: StakeholderRespondentType;
  scriptTemplateSheetUrl: string;
  contentsTemplateSheetUrl: string;
}

export interface UpdateStakeholderSettingInput {
  stakeholderSettingId: string;
  name?: string | null;
  respondentType?: StakeholderRespondentType | null;
  scriptTemplateSheetUrl?: string | null;
  contentsTemplateSheetUrl?: string | null;
}

export function useStakeholderSettings(termId: string | null) {
  const [result, reexecute] = useQuery({
    query: StakeholderSettingsQuery,
    variables: { termId: termId ?? "" },
    pause: !termId,
  });

  return {
    stakeholderSettings: result.data?.stakeholderSettings ?? [],
    loading: result.fetching,
    error: result.error
      ? toUserFacingMessage(result.error, "取得に失敗しました")
      : null,
    refetch: () => reexecute({ requestPolicy: "network-only" }),
  };
}

export function useStakeholderSettingMutations() {
  const [, executeCreate] = useMutation(CreateStakeholderSettingMutation);
  const [, executeUpdate] = useMutation(UpdateStakeholderSettingMutation);
  const [, executeDelete] = useMutation(DeleteStakeholderSettingMutation);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const createStakeholderSetting = useCallback(
    async (input: CreateStakeholderSettingInput): Promise<boolean> => {
      setLoading(true);
      setError(null);
      try {
        const response = await executeCreate({ input });
        if (response.error)
          throw new Error(
            toUserFacingMessage(response.error, "保存に失敗しました"),
          );
        return Boolean(response.data?.createStakeholderSetting);
      } catch (err) {
        setError(
          err instanceof Error ? err.message : "設定の作成に失敗しました",
        );
        return false;
      } finally {
        setLoading(false);
      }
    },
    [executeCreate],
  );

  const updateStakeholderSetting = useCallback(
    async (input: UpdateStakeholderSettingInput): Promise<boolean> => {
      setLoading(true);
      setError(null);
      try {
        const response = await executeUpdate({ input });
        if (response.error)
          throw new Error(
            toUserFacingMessage(response.error, "保存に失敗しました"),
          );
        return Boolean(response.data?.updateStakeholderSetting);
      } catch (err) {
        setError(
          err instanceof Error ? err.message : "設定の更新に失敗しました",
        );
        return false;
      } finally {
        setLoading(false);
      }
    },
    [executeUpdate],
  );

  const deleteStakeholderSetting = useCallback(
    async (stakeholderSettingId: string): Promise<boolean> => {
      setLoading(true);
      setError(null);
      try {
        const response = await executeDelete({ stakeholderSettingId });
        if (response.error)
          throw new Error(
            toUserFacingMessage(response.error, "保存に失敗しました"),
          );
        return Boolean(response.data?.deleteStakeholderSetting);
      } catch (err) {
        setError(
          err instanceof Error ? err.message : "設定の削除に失敗しました",
        );
        return false;
      } finally {
        setLoading(false);
      }
    },
    [executeDelete],
  );

  return {
    createStakeholderSetting,
    updateStakeholderSetting,
    deleteStakeholderSetting,
    loading,
    error,
  };
}
